import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/apiClient";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("login.welcome"));
      nav(loc.state?.from || "/dashboard");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-[80vh] grid grid-cols-1 lg:grid-cols-5 text-white">
      <div className="lg:col-span-3 hidden lg:block relative border-r border-white/10">
        <img
          src="https://images.unsplash.com/photo-1552925766-63ab07391e02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBkYXJrfGVufDB8fHx8MTc4NzYxNzkzMHww&ixlib=rb-4.1.0&q=85"
          alt="Reserved"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/50 to-transparent" />
        <div className="absolute bottom-12 left-12">
          <div className="overline mb-3">{t("login.heroOverline")}</div>
          <div className="font-display text-4xl max-w-md leading-tight">
            {t("login.heroTitle")}
          </div>
        </div>
      </div>

      <div className="lg:col-span-2 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="overline mb-3">{t("login.overline")}</div>
          <h1 className="font-display text-4xl mb-2">{t("login.title")}</h1>
          <p className="text-neutral-400 mb-10 text-sm">
            {t("login.subtitle")}
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">{t("login.email")}</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-black border border-white/20 text-white px-4 py-3 focus:border-[#E6C998] focus:outline-none focus:ring-1 focus:ring-[#E6C998]/40"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs uppercase tracking-wider text-neutral-400">{t("login.password")}</label>
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-link"
                  className="text-xs text-[#E6C998] hover:underline"
                >
                  {t("login.forgot")}
                </Link>
              </div>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-black border border-white/20 text-white px-4 py-3 focus:border-[#E6C998] focus:outline-none focus:ring-1 focus:ring-[#E6C998]/40"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-400">
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="btn-gold w-full py-4 rounded-none text-sm disabled:opacity-60"
            >
              {loading ? t("login.submitting") : t("login.submit")}
            </button>
          </form>

          <div className="divider-gold my-10" />

          <div className="text-sm text-neutral-400">
            {t("login.newHere")}{" "}
            <Link to="/register" className="text-[#E6C998] gold-underline">
              {t("login.openAccount")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
